import { ActiveSelection, Canvas, FabricObject } from 'fabric';

/** Options for {@link deleteObjects}. */
export interface DeleteObjectsOptions {
    /**
     * Called for each object just before it is removed — e.g. stop animations or release
     * locks tied to that object. Domain-specific cleanup lives here, keeping the core
     * removal logic shared.
     */
    onBeforeRemove?: (obj: FabricObject) => void;
}

/**
 * Remove `target` from the canvas — a single object, or every member of an
 * `ActiveSelection` — then clear the selection. Only objects actually on the canvas are
 * removed (guards against stale references). Framework-agnostic: `object:removed` lets
 * the attached sync binding (e.g. `FabricYjsBinding`) persist the deletion; callers wire
 * their own keyboard / delete-control triggers and decide who may delete.
 *
 * @returns the number of objects removed.
 */
export function deleteObjects(
    canvas: Canvas,
    target: FabricObject,
    options?: DeleteObjectsOptions
): number {
    const onCanvas = new Set(canvas.getObjects());
    const targets =
        target.type === 'activeselection'
            ? [...(target as ActiveSelection).getObjects()]
            : [target];

    let removed = 0;
    for (const obj of targets) {
        if (!onCanvas.has(obj)) continue; // only remove objects actually on the canvas
        options?.onBeforeRemove?.(obj);
        canvas.remove(obj);
        removed++;
    }

    if (removed > 0) {
        canvas.discardActiveObject();
        canvas.requestRenderAll();
    }
    return removed;
}
